import Session from "users/__session/session";

/**
 * The saves of a kept choice this tab has asked for and not yet had answered, one queue per key —
 * a table's columns (#6151), a panel's width and collapsed state (#6621).
 *
 * Every save carries the whole choice, and the server keeps the one it applies last. Sent side by
 * side, two quick changes could arrive in either order, and an older choice could overwrite a newer
 * one: the page here showed the newer choice, and the next visit, or another browser, showed the
 * older one.
 *
 * So a key's saves go one at a time, each after the one before it has its answer. A save that is
 * still waiting when a newer one joins is not sent at all, because the newer one carries the whole
 * choice. A burst of changes — an arrow key held down — therefore sends at most two requests.
 *
 * Each save is bound to the person who made it. Signing out, signing in as someone else and
 * impersonation all change who the request's token names, and a save still waiting in the queue
 * would otherwise be sent with the next person's token and land on their account.
 */
type KeyedQueue<TChoice> = {
  /** Settles when the last save to join has its answer. */
  tail: Promise<void>;
  /** Saves that joined and have not finished. */
  waiting: number;
  /** The place of the newest save to join. */
  newest: number;
  /** The newest save's choice and person, for an answer that arrives while it is still on its way. */
  newestChoice: TChoice;
  newestPrincipal: string | undefined;
};

/** A save's place in its key's queue. */
export type SavePlace = {
  /** Settles when every save before this one has its answer. */
  turn: Promise<void>;
  /** False once a newer save of the same key joins: this one then need not be sent. */
  isNewest: () => boolean;
  /** Called once, when this save has its answer or is not sent. */
  finish: () => void;
};

/** Who a session acts for: the account its token names. */
export function principalOf(session: Session | null | undefined): string | undefined {
  return session?.user?.id?.value;
}

/** Who the token that the next request will carry acts for. */
export function currentPrincipal(): string | undefined {
  try {
    return principalOf(Session.loadFromStorage(() => undefined));
  } catch {
    return undefined;
  }
}

export class ChoiceSaveQueue<TChoice> {
  public join(key: string, choice: TChoice, principal: string | undefined): SavePlace {
    const queue = this.queues.get(key) ?? {
      tail: Promise.resolve(),
      waiting: 0,
      newest: 0,
      newestChoice: choice,
      newestPrincipal: principal,
    };
    this.queues.set(key, queue);
    queue.newest += 1;
    queue.waiting += 1;
    queue.newestChoice = choice;
    queue.newestPrincipal = principal;
    const place = queue.newest;

    let answered: () => void = () => undefined;
    const done = new Promise<void>((resolve) => {
      answered = resolve;
    });
    const turn = queue.tail;
    queue.tail = turn.then(() => done);

    let finished = false;
    return {
      turn,
      isNewest: () => queue.newest === place,
      finish: () => {
        if (finished) return;
        finished = true;
        queue.waiting -= 1;
        if (queue.waiting === 0 && this.queues.get(key) === queue) this.queues.delete(key);
        answered();
      },
    };
  }

  /** Whether a save of the key — of any key, without one — has joined and not finished. */
  public hasPendingSave(key?: string): boolean {
    return key === undefined ? this.queues.size > 0 : this.queues.has(key);
  }

  /** Waits until no save of the key — of any key, without one — is waiting for its answer. */
  public async untilSavesAnswered(key?: string): Promise<void> {
    for (let tails = this.tailsOf(key); tails.length > 0; tails = this.tailsOf(key)) {
      await Promise.all(tails);
    }
  }

  /** The newest choice of each key with a save on its way, among the saves this person made. */
  public pendingChoices(principal: string | undefined): [string, TChoice][] {
    return [...this.queues.entries()]
      .filter(([, queue]) => queue.newestPrincipal === principal)
      .map(([key, queue]) => [key, queue.newestChoice]);
  }

  private readonly queues = new Map<string, KeyedQueue<TChoice>>();

  private tailsOf(key: string | undefined): Promise<void>[] {
    if (key !== undefined) {
      const queue = this.queues.get(key);
      return queue ? [queue.tail] : [];
    }
    return [...this.queues.values()].map((queue) => queue.tail);
  }
}
